import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Send as SendIcon,
    Notifications as NotificationsIcon,
    BarChart as StatsIcon,
    Inventory as ProductIcon,
    Article as ArticleIcon,
    Stars as SpecialIcon,
    Email as SimpleIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Card,
    CardContent,
    Autocomplete,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { pushNotificationsService } from '../services/pushNotificationsService';
import api from '../services/api';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingNotification, setEditingNotification] = useState(null);
    const [products, setProducts] = useState([]);
    const [articles, setArticles] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        type: 'info',
        targetType: 'all',
        scheduledAt: '',
        notificationStyle: 'simple',
        productId: null,
        articleId: null,
        specialData: {
            rewardType: 'points',
            rewardValue: 0,
            limitedQuantity: 0,
            expiresIn: null,
        },
        externalUrl: '',
        customImage: '',
    });
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchNotifications();
        fetchStats();
        fetchProducts();
        fetchArticles();
    }, []);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await pushNotificationsService.getAll(1, 100);
            setNotifications(response.data.notifications || []);
            setError('');
        } catch (error) {
            console.error('Erreur lors du chargement des notifications:', error);
            setError('Erreur lors du chargement des notifications');
            showNotification('Erreur lors du chargement des notifications', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await pushNotificationsService.getStats();
            setStats(response.data);
        } catch (error) {
            console.error('Erreur lors du chargement des statistiques:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await api.get('/api/products?limit=200');
            setProducts(response.data.products || []);
        } catch (error) {
            console.error('Erreur lors du chargement des produits:', error);
        }
    };

    const fetchArticles = async () => {
        try {
            const response = await api.get('/api/news?limit=200');
            setArticles(response.data.news || []);
        } catch (error) {
            console.error('Erreur lors du chargement des articles:', error);
        }
    };

    const handleCreate = () => {
        setEditingNotification(null);
        setFormData({
            title: '',
            body: '',
            type: 'info',
            targetType: 'all',
            scheduledAt: '',
            notificationStyle: 'simple',
            productId: null,
            articleId: null,
            specialData: {
                rewardType: 'points',
                rewardValue: 0,
                limitedQuantity: 0,
                expiresIn: null,
            },
            externalUrl: '',
            customImage: '',
        });
        setFormDialogOpen(true);
    };

    const handleEdit = (notification) => {
        setEditingNotification(notification);
        setFormData({
            title: notification.title,
            body: notification.body,
            type: notification.type,
            targetType: notification.targetType,
            scheduledAt: notification.scheduledAt || '',
            notificationStyle: notification.notificationStyle || 'simple',
            productId: notification.productId || null,
            articleId: notification.articleId || null,
            specialData: notification.specialData || {
                rewardType: 'points',
                rewardValue: 0,
                limitedQuantity: 0,
                expiresIn: null,
            },
            externalUrl: notification.externalUrl || '',
            customImage: notification.customImage || '',
        });
        setFormDialogOpen(true);
    };

    const handleDeleteClick = (notification) => {
        setSelectedNotification(notification);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await pushNotificationsService.delete(selectedNotification.id);
            showNotification('Notification supprimée avec succès', 'success');
            setDeleteDialogOpen(false);
            setSelectedNotification(null);
            fetchNotifications();
            fetchStats();
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            showNotification('Erreur lors de la suppression de la notification', 'error');
        }
    };

    const handleSendNow = async (notification) => {
        try {
            await pushNotificationsService.sendNow(notification.id);
            showNotification('Notification envoyée avec succès', 'success');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            showNotification('Erreur lors de l\'envoi de la notification', 'error');
        }
    };

    const handleFormSubmit = async () => {
        try {
            // Préparer les données
            const submitData = { ...formData };
            
            if (formData.notificationStyle !== 'special') {
                submitData.specialData = null;
            }
            
            if (formData.notificationStyle !== 'product') {
                submitData.productId = null;
            }
            
            if (formData.notificationStyle !== 'article') {
                submitData.articleId = null;
            }

            if (editingNotification) {
                await pushNotificationsService.update(editingNotification.id, submitData);
                showNotification('Notification mise à jour avec succès', 'success');
            } else {
                const response = await pushNotificationsService.create(submitData);
                const notification = response.data?.notification;
                if (notification?.notificationStyle === 'special' && notification?.code) {
                    showNotification(
                        `Notification créée avec succès! Code: ${notification.code}`,
                        'success'
                    );
                } else {
                    showNotification('Notification créée avec succès', 'success');
                }
            }
            setFormDialogOpen(false);
            setEditingNotification(null);
            fetchNotifications();
            fetchStats();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            showNotification('Erreur lors de la sauvegarde de la notification', 'error');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'sent':
                return 'success';
            case 'scheduled':
                return 'info';
            case 'sending':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'sent':
                return 'Envoyé';
            case 'scheduled':
                return 'Programmé';
            case 'sending':
                return 'En cours';
            case 'failed':
                return 'Échoué';
            default:
                return status;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'info':
                return 'Information';
            case 'promotion':
                return 'Promotion';
            case 'order_update':
                return 'Mise à jour commande';
            case 'system':
                return 'Système';
            default:
                return type;
        }
    };

    const getStyleLabel = (style) => {
        switch (style) {
            case 'simple':
                return 'Simple';
            case 'product':
                return 'Produit';
            case 'article':
                return 'Article';
            case 'special':
                return 'Spécial';
            default:
                return style;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Notifications Push</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Nouvelle notification
                </Button>
            </Box>

            {/* Statistiques */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Total
                                    </Typography>
                                </Box>
                                <Typography variant="h4">{stats.total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SendIcon sx={{ mr: 1, color: 'success.main' }} />
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Envoyées
                                    </Typography>
                                </Box>
                                <Typography variant="h4">{stats.sent}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <StatsIcon sx={{ mr: 1, color: 'info.main' }} />
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Programmées
                                    </Typography>
                                </Box>
                                <Typography variant="h4">{stats.scheduled}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SendIcon sx={{ mr: 1, color: 'error.main' }} />
                                    <Typography color="textSecondary" gutterBottom variant="body2">
                                        Total envoyé
                                    </Typography>
                                </Box>
                                <Typography variant="h4">{stats.totalSent || 0}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Titre</TableCell>
                            <TableCell>Style</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell>Envoyés</TableCell>
                            <TableCell>Programmée le</TableCell>
                            <TableCell>Créée le</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifications.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                        Aucune notification trouvée
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            notifications.map((notification) => (
                                <TableRow key={notification.id} hover>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="medium">
                                            {notification.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {notification.body.substring(0, 100)}...
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStyleLabel(notification.notificationStyle)}
                                            size="small"
                                            icon={notification.notificationStyle === 'product' ? <ProductIcon /> : 
                                                  notification.notificationStyle === 'article' ? <ArticleIcon /> :
                                                  notification.notificationStyle === 'special' ? <SpecialIcon /> : <SimpleIcon />}
                                            color="secondary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getTypeLabel(notification.type)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(notification.status)}
                                            color={getStatusColor(notification.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{notification.sentCount || 0}</TableCell>
                                    <TableCell>{formatDate(notification.scheduledAt)}</TableCell>
                                    <TableCell>{formatDate(notification.createdAt)}</TableCell>
                                    <TableCell align="right">
                                        {notification.status === 'scheduled' && (
                                            <Tooltip title="Envoyer maintenant">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSendNow(notification)}
                                                    color="primary"
                                                >
                                                    <SendIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {notification.status !== 'sending' && (
                                            <Tooltip title="Modifier">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(notification)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {notification.status !== 'sending' && (
                                            <Tooltip title="Supprimer">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteClick(notification)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog de formulaire */}
            <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingNotification ? 'Modifier la notification' : 'Nouvelle notification'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Titre"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    margin="normal"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    value={formData.body}
                                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                    margin="normal"
                                    required
                                    multiline
                                    rows={4}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={formData.type}
                                        label="Type"
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        <MenuItem value="info">Information</MenuItem>
                                        <MenuItem value="promotion">Promotion</MenuItem>
                                        <MenuItem value="order_update">Mise à jour commande</MenuItem>
                                        <MenuItem value="system">Système</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Style de notification</InputLabel>
                                    <Select
                                        value={formData.notificationStyle}
                                        label="Style de notification"
                                        onChange={(e) => setFormData({ ...formData, notificationStyle: e.target.value })}
                                    >
                                        <MenuItem value="simple">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <SimpleIcon sx={{ mr: 1 }} /> Simple (Direct)
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="product">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ProductIcon sx={{ mr: 1 }} /> Produit
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="article">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ArticleIcon sx={{ mr: 1 }} /> Article/Actualité
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="special">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <SpecialIcon sx={{ mr: 1 }} /> Spécial (Récompense)
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Style Product */}
                            {formData.notificationStyle === 'product' && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Produit" icon={<ProductIcon />} size="small" />
                                    </Divider>
                                    <Autocomplete
                                        options={products}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={products.find(p => p.id === formData.productId) || null}
                                        onChange={(event, newValue) => {
                                            setFormData({ ...formData, productId: newValue ? newValue.id : null });
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Sélectionner un produit" margin="normal" />
                                        )}
                                        fullWidth
                                    />
                                </Grid>
                            )}

                            {/* Style Article */}
                            {formData.notificationStyle === 'article' && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Article" icon={<ArticleIcon />} size="small" />
                                    </Divider>
                                    <Autocomplete
                                        options={articles}
                                        getOptionLabel={(option) => option.title || ''}
                                        value={articles.find(a => a.id === formData.articleId) || null}
                                        onChange={(event, newValue) => {
                                            setFormData({ 
                                                ...formData, 
                                                articleId: newValue ? newValue.id : null,
                                                articleTitle: newValue ? newValue.title : ''
                                            });
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Sélectionner un article" margin="normal" />
                                        )}
                                        fullWidth
                                    />
                                </Grid>
                            )}

                            {/* Style Special */}
                            {formData.notificationStyle === 'special' && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Notification Spéciale" icon={<SpecialIcon />} size="small" />
                                    </Divider>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Configuration de la récompense
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth margin="normal">
                                                <InputLabel>Type de récompense</InputLabel>
                                                <Select
                                                    value={formData.specialData.rewardType}
                                                    label="Type de récompense"
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        specialData: {
                                                            ...formData.specialData,
                                                            rewardType: e.target.value
                                                        }
                                                    })}
                                                >
                                                    <MenuItem value="points">Points de fidélité</MenuItem>
                                                    <MenuItem value="xp">XP</MenuItem>
                                                    <MenuItem value="voucher">Bon de réduction</MenuItem>
                                                    <MenuItem value="discount">Pourcentage de réduction</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Valeur de la récompense"
                                                type="number"
                                                value={formData.specialData.rewardValue}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    specialData: {
                                                        ...formData.specialData,
                                                        rewardValue: parseInt(e.target.value) || 0
                                                    }
                                                })}
                                                margin="normal"
                                                helperText={formData.specialData.rewardType === 'discount' ? 'Pourcentage (ex: 10)' : 'Valeur'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Nombre limité"
                                                type="number"
                                                value={formData.specialData.limitedQuantity}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    specialData: {
                                                        ...formData.specialData,
                                                        limitedQuantity: parseInt(e.target.value) || 0
                                                    }
                                                })}
                                                margin="normal"
                                                helperText="0 = illimité"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Expire dans (heures)"
                                                type="number"
                                                value={formData.specialData.expiresIn}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    specialData: {
                                                        ...formData.specialData,
                                                        expiresIn: e.target.value ? parseInt(e.target.value) : null
                                                    }
                                                })}
                                                margin="normal"
                                                helperText="Combien de temps la récompense est valide"
                                            />
                                        </Grid>
                                    </Grid>
                                    {editingNotification?.code && (
                                        <Box sx={{ mt: 2 }}>
                                            <Alert severity="info">
                                                Code généré: <strong>{editingNotification.code}</strong>
                                            </Alert>
                                        </Box>
                                    )}
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Cible</InputLabel>
                                    <Select
                                        value={formData.targetType}
                                        label="Cible"
                                        onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                                    >
                                        <MenuItem value="all">Tous les utilisateurs</MenuItem>
                                        <MenuItem value="users">Utilisateurs spécifiques</MenuItem>
                                        <MenuItem value="segment">Segment</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Date d'envoi programmée (optionnel)"
                                    type="datetime-local"
                                    value={formData.scheduledAt}
                                    onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                                    margin="normal"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormDialogOpen(false)}>Annuler</Button>
                    <Button
                        onClick={handleFormSubmit}
                        variant="contained"
                        disabled={!formData.title || !formData.body}
                    >
                        {editingNotification ? 'Mettre à jour' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de confirmation de suppression */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    Êtes-vous sûr de vouloir supprimer la notification "{selectedNotification?.title}" ?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Notifications;
